import React, { useState, useEffect } from 'react';
import { Close } from '@mui/icons-material';
import TextField from '@mui/material/TextField';
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import { truncateText } from "../../../features/stocks/RealTimeStock/RealTimeStockUtils";
import RenderSvg from "../../../components/renderSvg/RenderSvg";
import './ClaimRejectionModal.css';

interface ClaimRejectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  heading: string;
  selectedBatches: {
    id: string;  
    checked: boolean;
    type?: 'batch' | 'invoice' | 'others';
  }[];
  onConfirm: (reasons: Record<string, string>) => void;
  openClaimsRows?: any[]; 
  isBulkRejection?: boolean;
  claimType?: string; 
  areAllRejected?: (row: any) => boolean;
  approvalConfig?: any;
}

const ClaimRejectionModal: React.FC<ClaimRejectionModalProps> = ({
  isOpen,
  onClose,
  heading,
  selectedBatches,
  onConfirm,
  openClaimsRows,
  isBulkRejection = false,
  claimType,
  areAllRejected,
  approvalConfig
}) => {
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const { batchIdConfiguration, enableFocPurchaseReturnClaims } = ConfigDictionary;
  useEffect(() => {
    if (!isOpen) return;
  
    const itemsToCheck = isBulkRejection ? getBulkRejectionItems() : selectedBatches;
    if (itemsToCheck.length > 0) {
      const firstCheckedItem = itemsToCheck.find(item => item.checked);
      if (firstCheckedItem) {
        setExpandedItems({
          [firstCheckedItem.id]: true
        });
      }
    }
  }, [isOpen, selectedBatches, isBulkRejection]);  

  if (!isOpen) return null;

  const getBulkRejectionItems = () => {
    if (!openClaimsRows || !isBulkRejection) return [];
    
    return openClaimsRows.flatMap(row => {
      if (row.batchIds) {
        if (areAllRejected && areAllRejected(row)) return [];

        return row.batchIds
          .filter((batch: any) => {
            const hasLowerLevelRejection = batch.approvals?.some((approval: any) => {
              const approvalLevel = approvalConfig?.flow.findIndex((level:any) => 
                level.includes(approval.designation));
              return approval.status === 'REJECTED' && 
                     approvalLevel !== undefined && 
                     approvalLevel < (approvalConfig?.currentLevel ?? Infinity)
            })
            return !hasLowerLevelRejection && 
                   (!batch.status || batch.status !== "REJECTED")
          })
          .map((batch: any) => ({
            id: enableFocPurchaseReturnClaims && row.stockType
              ? `${batch.batchId}-${row.skuCode}-${row.returnOrderNo || ''}-${row.stockType}`
              : `${batch.batchId}-${row.skuCode}-${row.returnOrderNo || ''}`,
            checked: true,
            type: 'batch',
            skuCode: row.skuCode,
            returnOrderNo: row.returnOrderNo,
            stockType: row.stockType,
            batchId: batch.batchId,
            status: batch.status,
            rejectionReason: batch.rejectionReason,
            hasLowerLevelRejection: false,
          }));
          
      }
      else if (row.invoices) {
        if (areAllRejected && areAllRejected(row)) return [];
  
        return row.invoices
          .filter((invoice: any) => {
            const hasLowerLevelRejection = invoice.approvals?.some((approval: any) => {
              const approvalLevel = approvalConfig?.flow.findIndex((level:any) => 
                level.includes(approval.designation));
              return approval.status === 'REJECTED' && 
                     approvalLevel !== undefined && 
                     approvalLevel < (approvalConfig?.currentLevel ?? Infinity);
            });
            return !hasLowerLevelRejection && 
                   (!invoice.status || invoice.status !== "REJECTED");
          })
          .map((invoice: any) => ({
            id: batchIdConfiguration
            ? `${invoice.invoiceNumber}-${row.skuCode}-${row.remark || ''}-${invoice.batchIds}`
            : `${invoice.invoiceNumber}-${row.skuCode}-${row.remark || ''}`,
            checked: true,
            type: 'invoice',
            skuCode: row.skuCode,
            batchIds: invoice.batchIds,
            remark: row.remark,
            invoiceNumber: invoice.invoiceNumber,
            status: invoice.status,
            rejectionReason: invoice.rejectionReason,
            hasLowerLevelRejection: false
          }));
      }
      else if (claimType === "Others") {
        if (!row.status || row.status !== "REJECTED") {
            return [{
                id: row.outletCode,
                checked: true,
                type: 'others',
                outletCode: row.outletCode,
                outletName: row.outletName,
                status: row.status,
                rejectionReason: row.rejectionReason
            }];
        }
    }
      else {
        const hasLowerLevelRejection = row.approvals?.some((approval: any) => {
          const approvalLevel = approvalConfig?.flow.findIndex((level:any) => 
            level.includes(approval.designation));
          return approval.status === 'REJECTED' && 
                 approvalLevel !== undefined && 
                 approvalLevel < (approvalConfig?.currentLevel ?? Infinity);
        });
  
        if (!hasLowerLevelRejection && (!row.status || row.status !== "REJECTED")) {
          return [{
            id: row.id,
            checked: true,
            type: 'tov',
            invoiceNumber: row.invoiceNumber,
            invoiceDateTime: row.invoiceDateTime,
            status: row.status,
            rejectionReason: row.rejectionReason,
            hasLowerLevelRejection: false
          }];
        }
      }
      return [];
    });
  };

  const filteredItems = isBulkRejection ? getBulkRejectionItems() : 
    selectedBatches.filter(item => item.checked);

  const handleReasonChange = (id: string, reason: string) => {
    setReasons(prev => ({
      ...prev,
      [id]: reason
    }));
  };

  const toggleItemExpand = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleSubmit = () => {
    onConfirm(reasons);
    onClose();
  };

  const getItemTitle = (item: any) => {
    if (item.type === 'batch') {
      if (isBulkRejection) { return item.returnOrderNo ? `${item.skuCode} (${item.returnOrderNo}) - ${item.batchId}` : `${item.skuCode} - ${item.batchId}`; }
    }    
    if (item.type === 'invoice') {
      if (isBulkRejection) {
        return batchIdConfiguration
          ? `${item.skuCode} (${item.invoiceNumber}) - ${item.remark} - ${item.batchIds}`
          : `${item.skuCode} (${item.invoiceNumber}) - ${item.remark}`;
      }
    }
    if (item.type === 'tov') {
      return `${item.invoiceNumber}`;
    }
    if (item.type === 'others') {
      return `${item.outletCode} - ${item.outletName}`;
    }
    return item.id;
  };

  return (
    <div className="batch-rejection-modal-overlay" onWheel={(e) => e.stopPropagation()}>
      <div className="batch-rejection-modal">
        <div className="batch-rejection-modal-header">
          <div className="warning-icon-container">
            <div className="warning-icon">
              <span>!</span>
            </div>
            <button className="batch-rejection-modal-close" onClick={onClose}>
              <Close />
            </button>
          </div>
          <h3 className="batch-rejection-heading" title={heading}>{heading}</h3>
        </div>
        
        <div className="batch-rejection-modal-scroll-container">
          <div className="batch-rejection-modal-content">
            {filteredItems.length > 0 ? (
              filteredItems.map((item, index) => (
                <div key={index} className="batch-rejection-item">
                  <div 
                    className="batch-rejection-id"
                    onClick={() => toggleItemExpand(item.id)}
                  >
                    <p title={getItemTitle(item)} style={{ display: "inline-flex", alignItems: "center", gap: "2px" }}>{truncateText(getItemTitle(item),50)}{enableFocPurchaseReturnClaims && item.type === 'batch' && item.stockType === "foc" && (
                      <RenderSvg name="focIcon" />
                    )}</p>
                    {expandedItems[item.id] ? <ExpandLess /> : <ExpandMore />}
                  </div>
                  
                  {expandedItems[item.id] && (
                  <div className="batch-rejection-reason-container">
                    {(!item.status || item.status !== "REJECTED" || !item.rejectionReason) && (
                      <>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          placeholder="Please mention a reason for rejection."
                          value={reasons[item.id] || item.rejectionReason || ''}
                          onChange={(e) => handleReasonChange(item.id, e.target.value)}
                          inputProps={{ maxLength: 250 }}
                          className="rejection-reason-input"
                          disabled={!!item.rejectionReason && item.status === "REJECTED"}
                        />
                        <div className="batch-rejection-char-count">
                          {(reasons[item.id] || item.rejectionReason || '').length}/250 characters
                        </div>
                      </>
                    )}
                  </div>
                )}
                </div>
              ))
            ) : (
              <div className="no-batches-message">
                No items selected for rejection.
              </div>
            )}
          </div>
        </div>
        
        <div className="batch-rejection-modal-footer">
          <button 
            className="batch-rejection-continue-btn"
            onClick={handleSubmit}
            disabled={filteredItems.length === 0 || 
              filteredItems.some(item => !reasons[item.id]?.trim())}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClaimRejectionModal;